import React, { useContext, useState } from "react";
import "./StackCreator.css";

import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import Overlay from "../../../../../Overlay";
import { GreenMachineContext } from "../../GreenMachineContext";
import PlayerTableRow from "../../PlayerTable/PlayerTableRow";
import relayEnvironment from "../../../../../../RelayEnvironment";
import { COMMIT_STACK_RULE_CHANGES } from "./mutations/CommitStackRuleChanges";
import { isContentWriter, isSiteAdmin } from "../../../../../../AuthUtils";

const GET_PLAYERS = graphql`
  query StackCreatorPlayersQuery($greenMachineSessionID: ID!) {
    playersForGreenMachineSession(
      greenMachineSessionId: $greenMachineSessionID
      sortBy: "team"
    ) {
      id
      slatePlayer {
        id
        player {
          id
          name
          shortenedName
          team {
            abbreviation
            name
          }
        }
        position
        fppg
        salary
        rosterSlots {
          edges {
            node {
              name
            }
          }
        }
        slateCompetition {
          prettyStartTime
        }
        gupPercProj
        projPoints
      }
    }

    whoAmI {
      groups {
        name
      }
    }
  }
`;

const GET_RULES = graphql`
  query StackCreatorRulesQuery {
    greenMachineStackRules {
      id
      displayName
      operator
    }
  }
`;

export default function StackCreator(props) {
  const { greenMachineSessionID, numLineups } = useContext(GreenMachineContext);

  const [selectedPlayerIDs, setSelectedPlayerIDs] = useState([]);
  const [lockedPlayerIDs, setLockedPlayerIDs] = useState([]);
  const [errorMessage, setErrorMessage] = useState(undefined);

  const [selectedOperatorID, setSelectedOperatorID] = useState(undefined);
  const [selectedValue, setSelectedValue] = useState(undefined);
  const [stackRequired, setStackRequired] = useState(false);
  const [stackNumLineups, setStackNumLineups] = useState(undefined);
  const [stackName, setStackName] = useState(undefined);
  const [stackIsStaffStack, setStackIsStaffStack] = useState(false);

  const data = useLazyLoadQuery(GET_PLAYERS, {
    greenMachineSessionID: greenMachineSessionID,
  });

  const rulesData = useLazyLoadQuery(GET_RULES);

  if (!data) {
    return <></>;
  }

  function saveChanges() {
    setErrorMessage(undefined);

    if (stackIsStaffStack) {
      if (
        window.confirm(
          "Are you sure you want to make this stack public as a GC STAFF STACK?"
        )
      ) {
        if (!stackName) {
          alert("GC Staff Stacks must have a name.");
          return;
        }
      } else {
        return;
      }
    }

    commitMutation(relayEnvironment, {
      mutation: COMMIT_STACK_RULE_CHANGES,
      variables: {
        input: {
          greenMachineSessionId: greenMachineSessionID,
          stackRuleId: selectedOperatorID,
          stackValue: Number.parseInt(selectedValue),
          numLineups: Number.parseInt(stackNumLineups),
          required: stackRequired,
          players: selectedPlayerIDs,
          lockIds: lockedPlayerIDs,
          name: stackName,
          gcStaffStack: stackIsStaffStack,
        },
      },
      onCompleted: (content, errors) => {
        if (errors) {
          setErrorMessage(errors[0].message);
        } else {
          props.reloadStackList(
            {
              greenMachineSessionID: greenMachineSessionID,
            },
            { fetchPolicy: "network-only" }
          );

          setSelectedPlayerIDs([]);
          setLockedPlayerIDs([]);

          props.setShow(false);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });
  }

  function exitCreator(e) {
    e.preventDefault();

    setSelectedPlayerIDs([]);
    setLockedPlayerIDs([]);
    props.setShow(false);
  }

  function onOperatorChange(e) {
    setSelectedOperatorID(e.target.value);
  }

  function onValueChange(e) {
    setSelectedValue(e.target.value);
  }

  function onLineupsChange(e) {
    setStackNumLineups(e.target.value);
  }

  // These currently take Slate Player IDs, not Green Machine Session IDs
  function selectPlayer(player) {
    if (!selectedPlayerIDs.includes(player.id)) {
      setSelectedPlayerIDs([...selectedPlayerIDs, player.id]);
    }
  }

  // These currently take Slate Player IDs, not GreenMachineSession IDs
  function deselectPlayer(player) {
    const index = selectedPlayerIDs.indexOf(player.id);

    if (index > -1) {
      setSelectedPlayerIDs(
        selectedPlayerIDs.filter((item) => item !== player.id)
      );
    }
  }

  function togglePlayerLock(playerId) {
    if (!lockedPlayerIDs.includes(playerId)) {
      setLockedPlayerIDs([...lockedPlayerIDs, playerId]);
    } else {
      setLockedPlayerIDs(lockedPlayerIDs.filter((item) => item !== playerId));
    }
  }

  return (
    <Overlay show={props.show}>
      <div className="stack-creator-container">
        <h1>Stack Creator</h1>
        <button onClick={saveChanges}>Save Stack</button>{" "}
        <button onClick={exitCreator}>Exit Without Saving</button>
        <p className="stack-save-error">{errorMessage}</p>
        <p>
          Include{" "}
          <select
            name="stack-operator"
            id="stack-operator"
            onChange={onOperatorChange}
            onBlur={onOperatorChange}
          >
            <option selected disabled>
              ----
            </option>
            {rulesData
              ? rulesData.greenMachineStackRules.map((rule) => {
                  return (
                    <option key={rule.id} value={rule.id}>
                      {rule.displayName}
                    </option>
                  );
                })
              : null}
          </select>{" "}
          <select
            name="stack-value"
            id="stack-value"
            onChange={onValueChange}
            onBlur={onValueChange}
          >
            <option selected>0</option>
            {[
              ...Array(
                selectedPlayerIDs.length < 5 ? selectedPlayerIDs.length : 5
              ).keys(),
            ]
              .map((i) => i + 1)
              .map((val) => {
                return (
                  <option key={val} value={val}>
                    {val}
                  </option>
                );
              })}
          </select>{" "}
          from the following in{" "}
          <select
            name="num-lineups"
            id="num-lineups"
            onChange={onLineupsChange}
            onBlur={onLineupsChange}
          >
            <option selected disabled>
              --
            </option>
            {[...Array(numLineups).keys()]
              .map((i) => i + 1)
              .map((val) => {
                return (
                  <option key={val} value={val}>
                    {val}
                  </option>
                );
              })}
          </select>{" "}
          lineups.
        </p>
        {isSiteAdmin(data.whoAmI?.groups) ? (
          <p>
            <label>
              <input
                type="checkbox"
                checked={stackIsStaffStack}
                onChange={(e) => setStackIsStaffStack(e.target.checked)}
              />{" "}
              GC Staff Stack
            </label>
          </p>
        ) : null}
        <p>
          <label>
            Stack Name (optional): <br />
            <input
              type="text"
              className="stack-name"
              value={stackName}
              onChange={(e) => {
                if (e.target.value && e.target.value !== "") {
                  setStackName(e.target.value);
                } else {
                  setStackName(undefined);
                }
              }}
            />
          </label>
        </p>
        {data.playersForGreenMachineSession
          .filter((player) => selectedPlayerIDs.includes(player.slatePlayer.id))
          .map((player, index) => {
            return (
              <div key={player.id}>
                <PlayerTableRow
                  key={player.id}
                  player={player.slatePlayer}
                  updateUnsavedSelections={deselectPlayer}
                  selectedPlayers={selectedPlayerIDs}
                  sessionSelected={[]}
                  lastRow={index === selectedPlayerIDs.length - 1}
                  lockedPlayerIDs={lockedPlayerIDs}
                  togglePlayerLock={togglePlayerLock}
                />
              </div>
            );
          })}
        <hr />
        {data.playersForGreenMachineSession
          .filter(
            (player) => !selectedPlayerIDs.includes(player.slatePlayer.id)
          )
          .map((player, index, arr) => {
            return (
              <PlayerTableRow
                key={player.id}
                player={player.slatePlayer}
                updateUnsavedSelections={selectPlayer}
                selectedPlayers={[]}
                sessionSelected={[]}
                lastRow={index === arr.length - 1}
              />
            );
          })}
      </div>
    </Overlay>
  );
}
